import { NavLink, Outlet } from 'react-router';
import { PackageCheck } from 'lucide-react';
import {
  SECTION_TAB_ACTIVE,
  SECTION_TAB_BASE,
  SECTION_TAB_IDLE,
  SECTION_TAB_LIST,
} from '@/lib/section-tabs';

/**
 * DevicesFulfillmentShell — hardware we send WITHOUT a sale.
 *
 * The sibling entry, Devices Order, is the paid path: an invoice exists, a payment gate refuses
 * a coupon before it is settled, and a 60-day chase can switch an account off if it never is.
 * Nothing here has any of that. A demo coupon is issued because someone asked, on a staffer's
 * word alone — which is exactly why it is its own destination rather than a fourth tab wedged
 * into that flow, where it would read as part of a controlled process it has no part in.
 *
 * THREE tabs. RMA joined in 2026-08-25 and Amazon Coupons in 2026-09-22, each as a new entry in
 * TABS — exactly what this shell was created for: no rename, no moved URL, no round of dead
 * bookmarks. (`RMA / Fulfillment` is already one bucket in OptiClock's category list, so the
 * pairing is the org's own, not an invention here.)
 *
 * RMA is listed FIRST (requester, 2026-08-25) and it is the busiest: a queue customers fill by
 * submitting a form, versus two cards a staffer visits when someone asks.
 *
 * AMAZON COUPONS STRETCHES THIS SHELL'S THEME, and it is worth saying so rather than letting a
 * reader assume it fits. The other two send hardware with no sale attached. An Amazon coupon is a
 * discount on a sale that does happen — the customer pays Amazon, not us. It sits here because the
 * work is the same work (a staffer gives something away on their own word, with a reason and no
 * approver) and because the requester asked for it here (2026-09-22), not because the heading
 * above already covered it. The page description below was widened to stop claiming otherwise.
 *
 * Same shape as DeviceOrdersShell on purpose: the shell owns the h1 + icon, each child renders
 * its own description, and the sub-nav is plain NavLinks rather than the Tabs component — the
 * ledger underneath has its own filter row, and two identical tab bars stacked up read as one
 * confused control instead of a hierarchy.
 *
 * ROUTING NOTE: the RMA queue is the INDEX route, and Demo Coupons has moved to its own named
 * URL, `create-demo-coupon` (requester, 2026-08-25). So tab order and route order now agree: the
 * leftmost tab is what `/staff/devices-fulfillment` renders.
 *
 * THAT MOVE CHANGED AN EXISTING URL, which is the thing this shell's docblock otherwise exists to
 * avoid — `/staff/devices-fulfillment` used to render the demo card. Two redirects absorb it:
 * `/staff/devices-fulfillment/rma` still resolves (it shipped earlier the same day), and the older
 * `/staff/order-devices/demo` bookmark was RE-POINTED at `create-demo-coupon` rather than left
 * aimed at the section root — otherwise a link that used to open the demo card would silently open
 * the RMA queue instead, which is worse than a 404 because nothing looks wrong.
 */
const TABS = [
  // Labelled "RMA Requests", not the "RMA Coupons" this stub once said: v1 records and verifies
  // requests, it mints nothing. The documented replacement path is still a staff-created Shopify
  // order with a full custom discount, so a tab promising coupons would name a button that does
  // not exist. `end` because it is the index route — without it the link stays active on every
  // sibling path and both tabs would read as selected at once.
  { to: '.', end: true, label: 'RMA Requests', testId: 'tab-rma-requests' },
  // Labelled by PLATFORM (requester, 2026-09-24), to sit beside Amazon Coupons: these are Shopify
  // codes, redeemed in the portal's Order Devices checkout, and the sibling tab is Amazon's.
  //
  // The URL stays `create-demo-coupon` and the testId stays `tab-demo-coupons`. Renaming the route
  // is the one thing this shell's docblock exists to avoid — it would break every bookmark for a
  // label change — and the demo-coupon tables, service and module keep their names for the same
  // reason: the rename is what staff READ, not a refactor.
  { to: 'create-demo-coupon', end: false, label: 'Shopify Coupons', testId: 'tab-demo-coupons' },
  // Third tab, to the RIGHT of Demo Coupons (requester, 2026-09-22). Joined as a new TABS entry,
  // which is what this shell was built for — no rename, no moved URL, no dead bookmarks, unlike
  // the 2026-08-25 change the docblock above records.
  //
  // Its own shape differs from both siblings: it neither mints (Demo Coupons) nor projects an
  // external queue (RMA). It hands out codes a human pre-generated in Seller Central, because
  // Amazon publishes no API that creates one.
  { to: 'amazon-coupons', end: false, label: 'Amazon Coupons', testId: 'tab-amazon-coupons' },
];

export function DevicesFulfillmentShell() {
  return (
    <div className="flex h-full flex-col overflow-hidden" data-testid="devices-fulfillment-shell">
      <div className="px-6 pt-6">
        <h1 className="flex items-center gap-2 text-2xl font-bold text-foreground">
          <PackageCheck className="h-6 w-6" />
          {'Devices Fulfillment'}
        </h1>
        <p className="mt-1 text-sm text-muted-foreground">
          {'Hardware we send without a sale — a warranty replacement or a demo unit — and the '}
          {'Amazon coupons we hand to customers buying it themselves. No invoice, no payment, '}
          {'no chase.'}
        </p>
      </div>

      <nav className={`mt-4 ${SECTION_TAB_LIST}`} aria-label="Devices fulfillment sections">
        {TABS.map(({ to, end, label, testId }) => (
          <NavLink
            key={label}
            to={to}
            end={end}
            data-testid={testId}
            className={({ isActive }) =>
              [SECTION_TAB_BASE, isActive ? SECTION_TAB_ACTIVE : SECTION_TAB_IDLE].join(' ')
            }
          >
            {label}
          </NavLink>
        ))}
      </nav>

      <div className="min-h-0 flex-1 overflow-auto">
        <Outlet />
      </div>
    </div>
  );
}
